// v56.2 theme extraction — single source of truth for all colors, fonts, and brand constants.
// Every value here is copied verbatim from docs/parse-defi-swap-v56.2.jsx.

// ── Brand colors ─────────────────────────────────────────
export const BRAND_CYAN = '#00AAFF'
export const BRAND_GREEN = '#00CC66'
export const BRAND_GRADIENT = `linear-gradient(135deg, ${BRAND_CYAN}, ${BRAND_GREEN})`

// ── Font stacks ──────────────────────────────────────────
export const FONT_HEADING = "'Sora', sans-serif"
export const FONT_BODY = "'DM Sans', sans-serif"
export const FONT_MONO = "'Menlo', 'SF Mono', 'Monaco', 'Cascadia Mono', 'Roboto Mono', monospace"

// ── Theme color palette (22 keys) ────────────────────────
export interface ThemeColors {
  /** Page background */
  bg: string
  /** Card / panel surface */
  card: string
  /** Card border */
  cb: string
  /** Primary text */
  tx: string
  /** Secondary text */
  ts: string
  /** Muted text */
  mu: string
  /** Accent (interactive highlights) */
  ac: string
  /** Green (success, received amount) */
  gr: string
  /** Block background (input wells) */
  blB: string
  /** Action background (Half/Max buttons, chips) */
  aB: string
  /** Action color (text on action background) */
  aC: string
  /** Status / accent stripe color */
  st: string
  /** Divider / border */
  dv: string
  /** Modal overlay */
  mo: string
  /** Input background */
  iB: string
  /** Warning background */
  wB: string
  /** Warning color (text) */
  wC: string
  /** Error background */
  eB: string
  /** Error color (text) */
  eC: string
  /** OK / success background */
  oB: string
  /** Box shadow */
  sh: string
  /** Footer / caption color */
  fC: string
}

export const THEME_COLORS: Record<'light' | 'dark', ThemeColors> = {
  light: {
    bg: '#F0F7FF', card: '#FFFFFF', cb: '#D4E8FF', tx: '#0C1820',
    ts: '#3E6E94', mu: '#3E6E94', ac: '#006EAA',
    gr: '#007A3D', blB: '#E8F2FF',
    aB: '#E8F2FF', aC: '#006EAA', st: '#00CC66',
    dv: '#D8EEFF', mo: 'rgba(0,40,80,0.3)', iB: '#F0F7FF',
    wB: '#FFF4E4', wC: '#8B5F00', eB: '#FFF0F0', eC: '#CC3333',
    oB: '#E4FFF0', sh: '0 4px 24px rgba(0,80,160,0.08)',
    fC: '#3E6E94',
  },
  dark: {
    bg: '#060E18', card: '#0E1824', cb: '#1A3048', tx: '#C8DDE8',
    ts: '#6A98B8', mu: '#5A88A8', ac: '#44BBFF',
    gr: '#40EE88', blB: '#081C30',
    aB: '#081C30', aC: '#44AAFF', st: '#00CC66',
    dv: '#1A3048', mo: 'rgba(0,0,0,0.6)', iB: '#0A1420',
    wB: '#2A2008', wC: '#FFAA30', eB: '#2A0808', eC: '#FF6666',
    oB: '#082A10', sh: '0 4px 24px rgba(0,0,0,0.3)',
    fC: '#6A98B8',
  },
}

// ── Responsive UI sizing (4 viewport tiers) ──────────────
export interface UISizing {
  cardPad: string
  cardMax: number
  numFont: number
  usdFont: number
  swapSize: number
  swapIcon: number
  swapMargin: string
  ctaFont: number
  ctaPad: number
  tokIcon: number
  tokFont: number
  tokPad: string
  chainIcon: number
  chainFont: number
  chainPad: string
  headerLogo: number
  headerFont: number
  headerPad: string
}

export const UI_TIERS: Record<'small' | 'mobile' | 'desktop' | 'wide', UISizing> = {
  small: {
    cardPad: '20px 14px 18px', cardMax: 440,
    numFont: 24, usdFont: 11,
    swapSize: 40, swapIcon: 17, swapMargin: '10px 0',
    ctaFont: 15, ctaPad: 14,
    tokIcon: 22, tokFont: 13, tokPad: '5px 10px 5px 6px',
    chainIcon: 15, chainFont: 11, chainPad: '4px 8px',
    headerLogo: 28, headerFont: 13, headerPad: '12px 14px',
  },
  mobile: {
    cardPad: '24px 18px 22px', cardMax: 440,
    numFont: 28, usdFont: 12,
    swapSize: 42, swapIcon: 18, swapMargin: '12px 0',
    ctaFont: 16, ctaPad: 16,
    tokIcon: 23, tokFont: 14, tokPad: '6px 11px 6px 7px',
    chainIcon: 16, chainFont: 12, chainPad: '5px 9px',
    headerLogo: 38, headerFont: 16, headerPad: '14px 16px',
  },
  desktop: {
    cardPad: '32px 28px 26px', cardMax: 480,
    numFont: 36, usdFont: 13,
    swapSize: 48, swapIcon: 21, swapMargin: '16px 0',
    ctaFont: 18, ctaPad: 18,
    tokIcon: 28, tokFont: 16, tokPad: '8px 14px 8px 8px',
    chainIcon: 18, chainFont: 14, chainPad: '7px 11px',
    headerLogo: 46, headerFont: 19, headerPad: '16px 20px',
  },
  wide: {
    cardPad: '36px 32px 30px', cardMax: 520,
    numFont: 40, usdFont: 14,
    swapSize: 52, swapIcon: 23, swapMargin: '18px 0',
    ctaFont: 19, ctaPad: 20,
    tokIcon: 30, tokFont: 17, tokPad: '9px 15px 9px 9px',
    chainIcon: 19, chainFont: 15, chainPad: '8px 12px',
    headerLogo: 50, headerFont: 21, headerPad: '18px 24px',
  },
}
